import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  FaArrowLeft,
  FaHeart,
  FaHandHoldingHeart,
  FaUsers,
  FaPlusSquare,
  FaRibbon,
  FaHandsHelping,
  FaBullhorn,
  FaHandshake,
  FaSmile,
  FaGlobe,
  FaShieldAlt,
  FaBuilding,
  FaHospital,
  FaChild,
  FaUserMd,
  FaDonate,
  FaAward
} from 'react-icons/fa';

/* ============================================================
   LOCAL IMAGE IMPORTS
   Put these files inside:

   src/
      logos/
         cancerpatient/
            ginum-logo.png
            cancer1.png
            cancer2.png
            cancer3.png
            cancer4.png
            cancer5.png
   ============================================================ */

import ginumLogo from '../logos/Ginum/CPWS_GINUM.png';
import cancer1 from '../logos/Ginum/ginum1.png';
import cancer2 from '../logos/Ginum/ginum2.png';
import cancer3 from '../logos/Ginum/ginum3.png';
import cancer4 from '../logos/Ginum/ginum4.png';
import cancer5 from '../logos/Ginum/ginum5.png';


export default function CancerPatientWelfareSociety() {

  const navigate = useNavigate();

  /* ============================================================
     ANIMATION OBSERVER
     ============================================================ */

  useEffect(() => {
    const elements = document.querySelectorAll('.reveal');

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('show');
          }
        });
      },
      {
        threshold: 0.08
      }
    );

    elements.forEach(element => observer.observe(element));

    return () => observer.disconnect();
  }, []);


  return (
    <div className="cancer-page">

      {/* ======================================================
          BACK BUTTON
          ====================================================== */}

      <div className="top-navigation">
        <button
          className="back-button"
          onClick={() => navigate('/')}
        >
          <FaArrowLeft />
          <span>Back to All Sectors</span>
        </button>
      </div>


      {/* ======================================================
          MAIN PAGE
          ====================================================== */}

      <main className="poster-page">


        {/* ====================================================
            HERO SECTION
            ==================================================== */}

        <section className="hero-section reveal">

          {/* LEFT CONTENT */}

          <div className="hero-left">

            <div className="csr-badge">
              CSR INITIATIVE
            </div>

            <h1 className="hero-title">
              CANCER PATIENT
            </h1>

            <h1 className="hero-title-red">
              WELFARE SOCIETY
            </h1>

            <div className="collaboration">
              <span></span>
              <p>In Collaboration with</p>
              <span></span>
            </div>


            {/* LOCAL GINUM LOGO */}

            <div className="logo-wrapper">

              <img
                src={ginumLogo}
                alt="GINUM Cancer Patient Welfare Society"
                className="ginum-logo"
              />

            </div>


            <div className="hero-left-description">

              <h3>
                A COMMITMENT BEYOND BUSINESS.
                <br />
                <span>A RESPONSIBILITY FROM THE HEART.</span>
              </h3>

              <p>
                At Ashraf Group of Industries, success has never been
                measured only in numbers — but in the lives we touch.
              </p>

              <p>
                Our Cancer Patient Welfare Society, in collaboration
                with GINUM (Gujranwala Institute of Nuclear Medicine),
                stands as a symbol of hope for countless families
                battling cancer.
              </p>

            </div>

          </div>


          {/* RIGHT HERO IMAGE */}

          <div className="hero-right">

            <div className="quote-box">

              <div className="quote-mark">
                “
              </div>

              <p>
                In the face of illness,
                <br />
                humanity becomes our
                <br />
                greatest strength.
              </p>

              <div className="quote-heart">
                <FaHandHoldingHeart />
              </div>

            </div>


            <div className="hero-image-container">

              <img
                src={cancer1}
                alt="Cancer patient receiving care"
                className="hero-image"
              />

            </div>

          </div>

        </section>



        {/* ====================================================
            STANDING WITH PATIENTS
            ==================================================== */}

        <section className="standing-section reveal">

          <div className="standing-content">

            <div className="standing-text">

              <h2>
                STANDING WITH PATIENTS.
                <br />
                <span>SUPPORTING EVERY JOURNEY.</span>
              </h2>

              <p>
                Cancer is not just a disease — it is a test of strength,
                faith, and resilience. Through this initiative, we strive
                to ease that journey by supporting patients who need it
                the most.
              </p>

            </div>


            <div className="pillar-grid">

              <div className="pillar-card">
                <div className="pillar-icon">
                  <FaHandHoldingHeart />
                </div>

                <h4>
                  PROVIDING
                  <br />
                  FINANCIAL ASSISTANCE
                </h4>

                <p>
                  for treatment
                </p>
              </div>


              <div className="pillar-card">
                <div className="pillar-icon">
                  <FaUsers />
                </div>

                <h4>
                  SUPPORTING
                  <br />
                  UNDERPRIVILEGED
                  <br />
                  PATIENTS
                </h4>

                <p>
                  with care and dignity
                </p>
              </div>


              <div className="pillar-card">
                <div className="pillar-icon">
                  <FaPlusSquare />
                </div>

                <h4>
                  CONTRIBUTING TO
                  <br />
                  LIFE-SAVING
                  <br />
                  MEDICAL SERVICES
                </h4>
              </div>


              <div className="pillar-card">
                <div className="pillar-icon">
                  <FaHeart />
                </div>

                <h4>
                  ENSURING
                  <br />
                  NO ONE
                  <br />
                  FIGHTS ALONE
                </h4>
              </div>

            </div>

          </div>

        </section>



        {/* ====================================================
            PEOPLE OF GUJRANWALA
            ==================================================== */}

        <section className="people-section reveal">

          {/* IMAGE COLLAGE */}

          <div className="image-collage">

            <div className="collage-image image-large">

              <img
                src={cancer2}
                alt="GINUM hospital"
              />

            </div>


            <div className="collage-image">

              <img
                src={cancer3}
                alt="Cancer patient"
              />

            </div>


            <div className="collage-image">

              <img
                src={cancer4}
                alt="Doctor supporting patient"
              />

            </div>


            <div className="collage-image">

              <img
                src={cancer5}
                alt="Patient care"
              />

            </div>

          </div>


          {/* PEOPLE CONTENT */}

          <div className="people-content">

            <div className="people-heading">

              <FaHandHoldingHeart />

              <h2>
                POWERED BY THE <span>PEOPLE</span>
                <br />
                OF GUJRANWALA
              </h2>

              <FaHeart className="people-red-heart" />

            </div>


            <p className="people-description">

              This mission is not ours alone. It is made possible by
              the generosity, compassion, and unwavering support of
              the people of Gujranwala, who continue to stand with us
              in this fight against cancer.

            </p>


            <p className="people-bold">

              Together, we are building more than a welfare society —
              we are building hope, strength, and a future for those
              who need it most.

            </p>


            {/* STATISTICS */}

            <div className="statistics">

              <div className="stat-card">

                <FaUsers className="stat-icon" />

                <strong>
                  1000+
                </strong>

                <span>
                  Patients Supported
                  <br />
                  So Far
                </span>

              </div>


              <div className="stat-card">

                <FaHandHoldingHeart className="stat-icon" />

                <strong>
                  Millions
                </strong>

                <span>
                  Contributed Towards
                  <br />
                  Treatments
                </span>

              </div>


              <div className="stat-card">

                <FaHospital className="stat-icon" />

                <strong className="small-stat">
                  Strong Partnership
                </strong>

                <span>
                  With GINUM
                  <br />
                  Hospital
                </span>

              </div>


              <div className="stat-card">

                <FaHeart className="stat-icon" />

                <strong className="small-stat">
                  Countless Lives
                </strong>

                <span>
                  Touched With Care,
                  <br />
                  Support & Hope
                </span>

              </div>

            </div>

          </div>

        </section>



        {/* ====================================================
            HOPE SECTION
            ==================================================== */}

        <section className="hope-section reveal">

          <div className="hope-left">

            <div className="ribbon-box">

              <FaRibbon />

            </div>

            <div>

              <h2>
                HOPE. CARE. SUPPORT.
              </h2>

              <h3>
                TOGETHER WE CAN MAKE A DIFFERENCE.
              </h3>

              <p>
                Every contribution brings hope.
              </p>

              <p>
                Every act of kindness brings healing.
              </p>

              <p>
                Together, let's create a world where no cancer
                patient feels alone.
              </p>

            </div>

          </div>


          {/* ACTION AREA */}

          <div className="action-area">

            <h2>
              YOU CAN BE A PART OF THIS NOBLE CAUSE
            </h2>


            <div className="action-grid">

              <div className="action-card">

                <FaHandHoldingHeart />

                <h3>
                  DONATE
                </h3>

                <p>
                  Support treatment
                  <br />
                  of needy patients
                </p>

              </div>


              <div className="action-card">

                <FaHandsHelping />

                <h3>
                  VOLUNTEER
                </h3>

                <p>
                  Offer your time
                  <br />
                  and skills
                </p>

              </div>


              <div className="action-card">

                <FaBullhorn />

                <h3>
                  SPREAD AWARENESS
                </h3>

                <p>
                  Help us reach
                  <br />
                  more people
                </p>

              </div>


              <div className="action-card">

                <FaHandshake />

                <h3>
                  CORPORATE SUPPORT
                </h3>

                <p>
                  Partner with us
                  <br />
                  for greater impact
                </p>

              </div>

            </div>


            <div className="action-footer">

              TOGETHER, WE CAN BRING HOPE WHERE IT IS NEEDED THE MOST.

            </div>

          </div>

        </section>



        {/* ====================================================
            EXTRA IMAGE / IMPACT SECTION
            ==================================================== */}

        <section className="impact-section reveal">

          <div className="impact-image">

            <img
              src={cancer5}
              alt="Cancer care and support"
            />

          </div>


          <div className="impact-content">

            <div className="impact-icon">
              <FaAward />
            </div>

            <h2>
              MAKING AN IMPACT
              <br />
              THAT MATTERS
            </h2>

            <p>
              Every patient deserves dignity, care and hope.
              Our commitment is to help make that possible
              for families who need support during one of
              life's most difficult journeys.
            </p>

          </div>

        </section>



        {/* ====================================================
            FOOTER
            ==================================================== */}

        <footer className="poster-footer">

          <div className="footer-number">
            16
          </div>


          <div className="footer-company">

            <strong>
              ASHRAF GROUP OF INDUSTRIES
            </strong>

            <span>
              Building Legacy. Powering the Future.
            </span>

          </div>


          <div className="footer-pillars">

            <div>

              <FaSmile />

              <span>
                SERVING
                <br />
                HUMANITY
              </span>

            </div>


            <div>

              <FaShieldAlt />

              <span>
                COMMITTED
                <br />
                TO WELFARE
              </span>

            </div>


            <div>

              <FaUsers />

              <span>
                TOGETHER FOR
                <br />
                A BETTER TOMORROW
              </span>

            </div>


            <div>

              <FaGlobe />

              <span>
                MAKING IMPACT
                <br />
                THAT MATTERS
              </span>

            </div>

          </div>

        </footer>

      </main>


      {/* ======================================================
          CSS
          ====================================================== */}

      <style>{`

        * {
          box-sizing: border-box;
        }


        html {
          scroll-behavior: smooth;
        }


        body {
          margin: 0;
          padding: 0;
          overflow-x: hidden;
          background: #07152d;
        }


        .cancer-page {
          width: 100%;
          min-height: 100vh;
          background:
            radial-gradient(
              circle at top right,
              rgba(212, 163, 55, 0.08),
              transparent 35%
            ),
            #07152d;
          font-family:
            Arial,
            Helvetica,
            sans-serif;
          color: #152b4b;
          overflow-x: hidden;
        }


        /* ======================================================
           TOP NAV
           ====================================================== */

        .top-navigation {
          width: 100%;
          padding: 15px 25px;
          display: flex;
          justify-content: flex-start;
          background: #07152d;
          position: relative;
          z-index: 20;
        }


        .back-button {
          display: flex;
          align-items: center;
          gap: 9px;
          border: none;
          background: #b21f25;
          color: #ffffff;
          padding: 11px 20px;
          border-radius: 5px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 700;
          transition:
            transform 0.3s ease,
            background 0.3s ease,
            box-shadow 0.3s ease;
        }


        .back-button:hover {
          transform: translateX(5px);
          background: #8f171c;
          box-shadow:
            0 8px 20px rgba(178, 31, 37, 0.35);
        }


        /* ======================================================
           POSTER
           ====================================================== */

        .poster-page {
          width: 100%;
          max-width: 1920px;
          margin: 0 auto;
          background: #f9f7f2;
          overflow: hidden;
          box-shadow:
            0 0 50px rgba(0, 0, 0, 0.35);
        }


        /* ======================================================
           REVEAL ANIMATION
           ====================================================== */

        .reveal {
          opacity: 0;
          transform: translateY(45px);
          transition:
            opacity 0.9s ease,
            transform 0.9s ease;
        }


        .reveal.show {
          opacity: 1;
          transform: translateY(0);
        }


        /* ======================================================
           HERO
           ====================================================== */

        .hero-section {
          width: 100%;
          min-height: 590px;
          display: grid;
          grid-template-columns: 42% 58%;
          background: #f7f5f0;
        }


        .hero-left {
          padding:
            55px
            clamp(35px, 5vw, 100px)
            45px
            clamp(35px, 6vw, 110px);
          position: relative;
          background: #f7f5f0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          z-index: 2;
        }


        .hero-left::after {
          content: "";
          position: absolute;
          right: -75px;
          top: 0;
          width: 145px;
          height: 100%;
          background: #f7f5f0;
          clip-path: ellipse(65% 75% at 20% 50%);
          z-index: -1;
        }


        .csr-badge {
          align-self: flex-start;
          background: #173d68;
          color: white;
          font-size: 15px;
          letter-spacing: 1px;
          font-weight: 800;
          padding: 9px 23px;
          border-radius: 0 25px 25px 0;
          margin-bottom: 24px;
          animation: slideRight 0.8s ease both;
        }


        .hero-title {
          margin: 0;
          color: #173b66;
          font-family:
            Georgia,
            "Times New Roman",
            serif;
          font-size: clamp(40px, 4.2vw, 74px);
          line-height: 0.98;
          font-weight: 900;
          letter-spacing: -1px;
          animation: titleReveal 1s ease both;
        }


        .hero-title-red {
          margin: 7px 0 22px;
          color: #a92527;
          font-family:
            Georgia,
            "Times New Roman",
            serif;
          font-size: clamp(40px, 4.2vw, 74px);
          line-height: 0.98;
          font-weight: 900;
          letter-spacing: -1px;
          animation: titleReveal 1.15s ease both;
        }


        .collaboration {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 10px;
        }


        .collaboration span {
          width: 70px;
          height: 1px;
          background: #7d8996;
        }


        .collaboration p {
          margin: 0;
          font-size: 15px;
          color: #35465a;
          font-weight: 600;
        }


        .logo-wrapper {
          width: 240px;
          margin-top: 3px;
          margin-bottom: 15px;
        }


        .ginum-logo {
          width: 100%;
          max-width: 240px;
          height: auto;
          display: block;
          object-fit: contain;
          transition:
            transform 0.5s ease,
            filter 0.5s ease;
        }


        .ginum-logo:hover {
          transform: scale(1.06);
          filter:
            drop-shadow(
              0 10px 15px rgba(0, 0, 0, 0.15)
            );
        }


        .hero-left-description {
          max-width: 580px;
          margin-top: 5px;
        }


        .hero-left-description h3 {
          color: #173d68;
          font-size: 18px;
          line-height: 1.25;
          margin: 0 0 14px;
          font-weight: 900;
        }


        .hero-left-description h3 span {
          color: #b27b18;
        }


        .hero-left-description p {
          margin: 0 0 10px;
          color: #485665;
          font-size: 14px;
          line-height: 1.55;
        }


        /* ======================================================
           HERO RIGHT
           ====================================================== */

        .hero-right {
          position: relative;
          min-height: 590px;
          overflow: hidden;
          background: #e7e4de;
        }


        .hero-image-container {
          position: absolute;
          inset: 0;
          overflow: hidden;
        }


        .hero-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
          transition:
            transform 1.2s ease;
        }


        .hero-right:hover .hero-image {
          transform: scale(1.035);
        }


        .quote-box {
          position: absolute;
          right: 35px;
          top: 40px;
          width: 240px;
          z-index: 5;
          text-align: center;
          color: #173d68;
          background: rgba(255, 255, 255, 0.78);
          padding: 22px 15px 18px;
          backdrop-filter: blur(5px);
          border-radius: 5px;
          animation:
            quoteFloat 4s ease-in-out infinite;
        }


        .quote-mark {
          font-family: Georgia, serif;
          color: #173d68;
          font-size: 70px;
          line-height: 0.5;
          font-weight: bold;
        }


        .quote-box p {
          margin: 12px 0 12px;
          font-size: 18px;
          line-height: 1.35;
          font-weight: 800;
        }


        .quote-heart {
          color: #b27b18;
          font-size: 36px;
        }


        /* ======================================================
           STANDING SECTION
           ====================================================== */

        .standing-section {
          width: 100%;
          background: #082c55;
          color: #ffffff;
          padding: 38px clamp(25px, 6vw, 110px);
          position: relative;
        }


        .standing-content {
          width: 100%;
          max-width: 1500px;
          margin: auto;
        }


        .standing-text {
          max-width: 850px;
          margin-bottom: 30px;
        }


        .standing-text h2 {
          margin: 0 0 13px;
          font-size: clamp(22px, 2vw, 34px);
          line-height: 1.12;
          font-weight: 900;
        }


        .standing-text h2 span {
          color: #e4aa28;
        }


        .standing-text p {
          margin: 0;
          color: #d9e2ec;
          max-width: 900px;
          font-size: 15px;
          line-height: 1.6;
        }


        .pillar-grid {
          display: grid;
          grid-template-columns:
            repeat(4, 1fr);
          border-top: 1px solid rgba(255,255,255,0.3);
        }


        .pillar-card {
          min-height: 170px;
          padding: 25px 18px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          border-right:
            1px solid rgba(255,255,255,0.35);
          transition:
            transform 0.4s ease,
            background 0.4s ease;
        }


        .pillar-card:last-child {
          border-right: none;
        }


        .pillar-card:hover {
          transform: translateY(-8px);
          background:
            rgba(255,255,255,0.06);
        }


        .pillar-icon {
          width: 66px;
          height: 66px;
          border-radius: 50%;
          background: #fff;
          color: #173d68;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          margin-bottom: 12px;
          transition:
            transform 0.4s ease;
        }


        .pillar-card:hover .pillar-icon {
          transform:
            rotateY(180deg);
        }


        .pillar-card h4 {
          margin: 0;
          font-size: 13px;
          line-height: 1.35;
          color: #ffffff;
        }


        .pillar-card p {
          margin: 5px 0 0;
          color: #dbe4ed;
          font-size: 11px;
        }


        /* ======================================================
           PEOPLE SECTION
           ====================================================== */

        .people-section {
          width: 100%;
          padding: 45px clamp(20px, 6vw, 110px);
          display: grid;
          grid-template-columns: 45% 55%;
          gap: 50px;
          background: #f8f6f1;
          align-items: center;
        }


        .image-collage {
          display: grid;
          grid-template-columns: 1.35fr 0.8fr;
          grid-template-rows:
            repeat(2, minmax(190px, 1fr));
          gap: 5px;
          height: 430px;
          border-radius: 16px;
          overflow: hidden;
        }


        .collage-image {
          overflow: hidden;
          position: relative;
        }


        .collage-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition:
            transform 0.7s ease;
        }


        .collage-image:hover img {
          transform: scale(1.08);
        }


        .image-large {
          grid-row: span 2;
        }


        .people-content {
          max-width: 850px;
        }


        .people-heading {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 18px;
        }


        .people-heading > svg {
          font-size: 40px;
          color: #173d68;
          flex-shrink: 0;
        }


        .people-heading h2 {
          margin: 0;
          color: #173d68;
          font-size: clamp(22px, 2vw, 34px);
          line-height: 1.1;
          font-weight: 900;
        }


        .people-heading h2 span {
          color: #a92527;
        }


        .people-heading .people-red-heart {
          color: #a92527;
          font-size: 28px;
        }


        .people-description {
          color: #465566;
          font-size: 15px;
          line-height: 1.65;
          margin: 0 0 15px;
        }


        .people-bold {
          color: #173d68;
          font-size: 16px;
          line-height: 1.55;
          margin: 0 0 25px;
          font-weight: 800;
        }


        /* ======================================================
           STATISTICS
           ====================================================== */

        .statistics {
          display: grid;
          grid-template-columns:
            repeat(4, 1fr);
          border-top: 1px solid #d6c9a9;
          padding-top: 20px;
        }


        .stat-card {
          padding:
            0 15px;
          min-height: 110px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          border-right:
            1px solid #d7c9a8;
        }


        .stat-card:last-child {
          border-right: none;
        }


        .stat-icon {
          font-size: 28px;
          color: #173d68;
          margin-bottom: 7px;
        }


        .stat-card strong {
          color: #a92527;
          font-size: 21px;
          line-height: 1.1;
          font-weight: 900;
          margin-bottom: 4px;
        }


        .stat-card .small-stat {
          font-size: 14px;
          min-height: 25px;
          display: flex;
          align-items: center;
        }


        .stat-card span {
          color: #4b5867;
          font-size: 10px;
          line-height: 1.3;
          font-weight: 600;
        }


        /* ======================================================
           HOPE SECTION
           ====================================================== */

        .hope-section {
          width: 100%;
          display: grid;
          grid-template-columns: 40% 60%;
          background: #f7f4ec;
          padding: 0 clamp(20px, 4vw, 70px) 35px;
        }


        .hope-left {
          background: #082c55;
          color: white;
          min-height: 300px;
          padding:
            45px clamp(25px, 5vw, 75px);
          display: flex;
          align-items: center;
          gap: 28px;
          border-radius:
            0 0 65px 0;
          position: relative;
          overflow: hidden;
        }


        .hope-left::before {
          content: "";
          position: absolute;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,0.08);
          right: -70px;
          top: -70px;
        }


        .ribbon-box {
          flex-shrink: 0;
        }


        .ribbon-box svg {
          color: #e4aa28;
          font-size: 85px;
          animation:
            ribbonPulse 2.5s ease-in-out infinite;
        }


        .hope-left h2 {
          color: #ffffff;
          font-size: clamp(24px, 2.3vw, 37px);
          line-height: 1;
          margin: 0 0 8px;
          font-weight: 900;
        }


        .hope-left h3 {
          color: #e5aa27;
          font-size: 13px;
          letter-spacing: 1px;
          margin: 0 0 18px;
        }


        .hope-left p {
          color: #dbe4ed;
          margin: 5px 0;
          font-size: 13px;
          line-height: 1.4;
        }


        /* ======================================================
           ACTION AREA
           ====================================================== */

        .action-area {
          padding:
            25px clamp(15px, 3vw, 40px);
          border:
            1px solid #d9cba9;
          border-radius:
            0 0 25px 25px;
          background: #fffdf8;
        }


        .action-area > h2 {
          text-align: center;
          background: #f6f2e9;
          color: #173d68;
          margin: 0 0 22px;
          padding: 13px;
          border-radius: 5px;
          font-size: clamp(15px, 1.5vw, 23px);
          letter-spacing: 1px;
          font-weight: 900;
        }


        .action-grid {
          display: grid;
          grid-template-columns:
            repeat(4, 1fr);
          gap: 12px;
        }


        .action-card {
          text-align: center;
          padding: 16px 10px;
          border-right:
            1px solid #d9cba9;
          transition:
            transform 0.35s ease,
            background 0.35s ease;
        }


        .action-card:last-child {
          border-right: none;
        }


        .action-card:hover {
          transform: translateY(-7px);
          background: #faf5e9;
        }


        .action-card > svg {
          font-size: 36px;
          color: #173d68;
          margin-bottom: 9px;
          transition:
            transform 0.4s ease;
        }


        .action-card:hover > svg {
          transform:
            scale(1.15)
            rotate(-5deg);
        }


        .action-card h3 {
          margin: 0 0 6px;
          color: #a92527;
          font-size: 12px;
          font-weight: 900;
        }


        .action-card p {
          margin: 0;
          color: #667383;
          font-size: 10px;
          line-height: 1.35;
        }


        .action-footer {
          margin-top: 15px;
          background: #173d68;
          color: white;
          text-align: center;
          padding: 12px;
          border-radius: 5px;
          font-weight: 800;
          font-size: 11px;
          letter-spacing: 0.8px;
        }


        /* ======================================================
           IMPACT
           ====================================================== */

        .impact-section {
          display: grid;
          grid-template-columns: 45% 55%;
          min-height: 300px;
          background: #082c55;
        }


        .impact-image {
          overflow: hidden;
        }


        .impact-image img {
          width: 100%;
          height: 100%;
          min-height: 300px;
          object-fit: cover;
          display: block;
          transition:
            transform 0.9s ease;
        }


        .impact-image:hover img {
          transform: scale(1.06);
        }


        .impact-content {
          color: white;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding:
            45px clamp(30px, 7vw, 120px);
        }


        .impact-icon {
          color: #e4aa28;
          font-size: 38px;
          margin-bottom: 10px;
        }


        .impact-content h2 {
          margin: 0 0 15px;
          font-size: clamp(25px, 3vw, 44px);
          line-height: 1.05;
          font-weight: 900;
        }


        .impact-content p {
          max-width: 650px;
          color: #d8e2ec;
          line-height: 1.65;
          font-size: 14px;
          margin: 0;
        }


        /* ======================================================
           FOOTER
           ====================================================== */

        .poster-footer {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 25px;
          padding:
            18px clamp(20px, 4vw, 65px);
          background: #f7f5f0;
          border-top: 5px solid #173d68;
        }


        .footer-number {
          color: #173d68;
          font-family: Georgia, serif;
          font-size: clamp(50px, 5vw, 82px);
          font-weight: 900;
          line-height: 0.8;
        }


        .footer-company {
          min-width: 260px;
          display: flex;
          flex-direction: column;
          border-right:
            1px solid #cfc6b2;
          padding-right: 30px;
        }


        .footer-company strong {
          color: #173d68;
          font-size: 17px;
          letter-spacing: 0.6px;
        }


        .footer-company span {
          color: #b27b18;
          font-size: 12px;
          margin-top: 5px;
        }


        .footer-pillars {
          flex: 1;
          display: grid;
          grid-template-columns:
            repeat(4, 1fr);
        }


        .footer-pillars > div {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          padding: 0 15px;
          border-right:
            1px solid #d3c9b4;
          text-align: center;
        }


        .footer-pillars > div:last-child {
          border-right: none;
        }


        .footer-pillars svg {
          color: #173d68;
          font-size: 24px;
          flex-shrink: 0;
        }


        .footer-pillars span {
          color: #263c54;
          font-size: 9px;
          line-height: 1.35;
          font-weight: 900;
        }


        /* ======================================================
           KEYFRAMES
           ====================================================== */

        @keyframes titleReveal {

          from {
            opacity: 0;
            transform:
              translateX(-35px);
          }

          to {
            opacity: 1;
            transform:
              translateX(0);
          }

        }


        @keyframes slideRight {

          from {
            opacity: 0;
            transform:
              translateX(-50px);
          }

          to {
            opacity: 1;
            transform:
              translateX(0);
          }

        }


        @keyframes quoteFloat {

          0%,
          100% {
            transform:
              translateY(0);
          }

          50% {
            transform:
              translateY(-9px);
          }

        }


        @keyframes ribbonPulse {

          0%,
          100% {
            transform:
              rotate(-3deg)
              scale(1);
          }

          50% {
            transform:
              rotate(3deg)
              scale(1.07);
          }

        }


        /* ======================================================
           TABLET
           ====================================================== */

        @media (max-width: 1100px) {

          .hero-section {
            grid-template-columns: 48% 52%;
          }


          .hero-left {
            padding:
              45px 45px 40px;
          }


          .hero-title,
          .hero-title-red {
            font-size: 45px;
          }


          .people-section {
            gap: 30px;
          }


          .hope-section {
            grid-template-columns: 1fr;
            padding-left: 20px;
            padding-right: 20px;
          }


          .hope-left {
            border-radius:
              0 0 45px 45px;
          }

        }


        /* ======================================================
           TABLET / MOBILE
           ====================================================== */

        @media (max-width: 850px) {

          .hero-section {
            grid-template-columns: 1fr;
          }


          .hero-left {
            padding:
              45px 35px 35px;
          }


          .hero-left::after {
            display: none;
          }


          .hero-right {
            min-height: 450px;
          }


          .quote-box {
            top: 20px;
            right: 20px;
            width: 210px;
          }


          .standing-section {
            padding:
              35px 25px;
          }


          .pillar-grid {
            grid-template-columns:
              repeat(2, 1fr);
          }


          .pillar-card:nth-child(2) {
            border-right: none;
          }


          .people-section {
            grid-template-columns: 1fr;
            padding:
              40px 25px;
          }


          .image-collage {
            height: 400px;
          }


          .hope-left {
            min-height: auto;
            padding: 40px 30px;
          }


          .impact-section {
            grid-template-columns: 1fr;
          }


          .impact-image {
            height: 350px;
          }


          .poster-footer {
            flex-wrap: wrap;
          }


          .footer-pillars {
            width: 100%;
          }

        }


        /* ======================================================
           MOBILE
           ====================================================== */

        @media (max-width: 600px) {

          .top-navigation {
            padding:
              12px 12px;
          }


          .back-button {
            padding:
              9px 13px;
            font-size: 11px;
          }


          .hero-left {
            padding:
              35px 20px 30px;
          }


          .csr-badge {
            font-size: 10px;
            padding:
              8px 15px;
          }


          .hero-title,
          .hero-title-red {
            font-size:
              clamp(31px, 10vw, 43px);
          }


          .collaboration p {
            font-size: 11px;
          }


          .collaboration span {
            width: 35px;
          }


          .logo-wrapper {
            width: 190px;
          }


          .ginum-logo {
            max-width: 190px;
          }


          .hero-left-description h3 {
            font-size: 14px;
          }


          .hero-left-description p {
            font-size: 12px;
          }


          .hero-right {
            min-height: 380px;
          }


          .quote-box {
            width:
              170px;
            padding:
              12px 8px;
            top: 12px;
            right: 12px;
          }


          .quote-mark {
            font-size: 48px;
          }


          .quote-box p {
            font-size: 12px;
          }


          .quote-heart {
            font-size: 25px;
          }


          .standing-section {
            padding:
              30px 15px;
          }


          .standing-text h2 {
            font-size: 21px;
          }


          .standing-text p {
            font-size: 12px;
          }


          .pillar-grid {
            grid-template-columns: 1fr;
          }


          .pillar-card {
            border-right: none;
            border-bottom:
              1px solid rgba(255,255,255,0.25);
            min-height: 145px;
          }


          .pillar-card:last-child {
            border-bottom: none;
          }


          .people-section {
            padding:
              30px 15px;
          }


          .image-collage {
            height: 320px;
            grid-template-columns:
              1.3fr 0.8fr;
            gap: 3px;
          }


          .people-heading {
            align-items: flex-start;
            gap: 8px;
          }


          .people-heading > svg {
            font-size: 28px;
          }


          .people-heading h2 {
            font-size: 20px;
          }


          .people-heading .people-red-heart {
            font-size: 19px;
          }


          .people-description {
            font-size: 12px;
          }


          .people-bold {
            font-size: 13px;
          }


          .statistics {
            grid-template-columns:
              repeat(2, 1fr);
            gap: 18px 0;
          }


          .stat-card {
            border-right: none;
            border-bottom:
              1px solid #ddd4c3;
            padding-bottom: 14px;
          }


          .stat-card:nth-child(odd) {
            border-right:
              1px solid #d7c9a8;
          }


          .stat-card:nth-child(3),
          .stat-card:nth-child(4) {
            border-bottom: none;
          }


          .hope-section {
            padding:
              0 12px 25px;
          }


          .hope-left {
            flex-direction: column;
            text-align: center;
            padding:
              35px 20px;
            gap: 10px;
          }


          .ribbon-box svg {
            font-size: 65px;
          }


          .hope-left h2 {
            font-size: 26px;
          }


          .hope-left h3 {
            font-size: 10px;
          }


          .hope-left p {
            font-size: 11px;
          }


          .action-area {
            padding:
              18px 10px;
          }


          .action-area > h2 {
            font-size: 13px;
            letter-spacing: 0.5px;
          }


          .action-grid {
            grid-template-columns:
              repeat(2, 1fr);
          }


          .action-card {
            border-right: none;
            border-bottom:
              1px solid #d9cba9;
          }


          .action-card:nth-child(3),
          .action-card:nth-child(4) {
            border-bottom: none;
          }


          .action-footer {
            font-size: 9px;
            line-height: 1.35;
          }


          .impact-image {
            height: 260px;
          }


          .impact-content {
            padding:
              35px 22px;
          }


          .impact-content h2 {
            font-size: 27px;
          }


          .impact-content p {
            font-size: 12px;
          }


          .poster-footer {
            padding:
              20px 15px;
            gap: 15px;
          }


          .footer-number {
            font-size: 52px;
          }


          .footer-company {
            min-width: 0;
            padding-right: 10px;
          }


          .footer-company strong {
            font-size: 11px;
          }


          .footer-company span {
            font-size: 8px;
          }


          .footer-pillars {
            grid-template-columns:
              repeat(2, 1fr);
            gap: 15px 0;
          }


          .footer-pillars > div {
            border-right: none;
          }


          .footer-pillars span {
            font-size: 7px;
          }


          .footer-pillars svg {
            font-size: 18px;
          }

        }


        /* ======================================================
           VERY SMALL MOBILE
           ====================================================== */

        @media (max-width: 380px) {

          .hero-title,
          .hero-title-red {
            font-size: 29px;
          }


          .hero-right {
            min-height: 320px;
          }


          .quote-box {
            width: 145px;
          }


          .quote-box p {
            font-size: 10px;
          }


          .image-collage {
            height: 270px;
          }


          .action-grid {
            grid-template-columns: 1fr;
          }


          .action-card {
            border-bottom:
              1px solid #d9cba9 !important;
          }


          .action-card:last-child {
            border-bottom: none !important;
          }


          .footer-number {
            font-size: 45px;
          }


          .footer-company strong {
            font-size: 9px;
          }

        }

      `}</style>

    </div>
  );
}